import { useEffect, useMemo } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Loader2, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAttribution } from '@/hooks/useAttribution';

interface PublicRestaurantData {
  id: string;
  name: string;
}

export default function PublicRestaurantMenu() {
  const [, params] = useRoute('/r/:eatzyId');
  const eatzyId = params?.eatzyId;
  const [, navigate] = useLocation();
  const { sendAttributionEvent } = useAttribution();

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const source = searchParams.get('source');
  const highlightItemId = searchParams.get('item');

  useEffect(() => {
    if (source) {
      try {
        sessionStorage.setItem('social_source', source);
        sessionStorage.setItem('social_landing_url', window.location.href);
      } catch {}
    }
  }, [source]);

  useEffect(() => {
    if (eatzyId) {
      sendAttributionEvent('menu_view', 'restaurant', String(eatzyId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eatzyId]);

  const { data: restaurant, isLoading, error } = useQuery<PublicRestaurantData>({
    queryKey: ['/api/public/restaurant', eatzyId],
    enabled: !!eatzyId,
  });

  useEffect(() => {
    if (!restaurant?.id) return;
    const target = new URLSearchParams();
    if (highlightItemId) target.set('item', highlightItemId);
    if (source) target.set('source', source);
    const qs = target.toString();
    navigate(`/restaurant/${restaurant.id}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [restaurant?.id, highlightItemId, source, navigate]);

  if (isLoading || restaurant?.id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || (!isLoading && !restaurant)) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <UtensilsCrossed className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-xl font-semibold mb-2">Restaurant Not Found</h1>
        <p className="text-muted-foreground mb-6">
          This restaurant link may be outdated or the restaurant is no longer available.
        </p>
        <Button onClick={() => navigate('/')}>
          Browse Restaurants
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
